import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { AlertStatusIcon, CloseIcon } from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';

/**
 * F43 (Track D design-pass) — Fullscreen image lightbox / evidence viewer.
 *
 * Used by the admin report-detail page to view report-evidence screenshots
 * (no fullscreen/zoom image primitive existed before — `PhotoCarousel` is
 * inline-only, `ImageUploader` is upload-only; see plan §6 design flag 1).
 *
 * Production component: accessible modal (portal + focus trap, Escape +
 * backdrop-click close, role=dialog aria-modal, body-scroll lock), a centred
 * responsive image with loading + error states, a close button, and an
 * optional caption derived from `alt`. Token-driven (no raw hex), RTL-safe via
 * logical properties.
 *
 * The exported prop signature is FROZEN ({ src, alt?, open, onClose }) so
 * Track B can build against it. Renders `null` when `!open`.
 */
export interface ImageLightboxProps {
  /** The image source URL (a presigned GET for report evidence). */
  readonly src: string;
  /** Accessible alt text. Doubles as the visible caption when present. */
  readonly alt?: string;
  /** Whether the lightbox is open. */
  readonly open: boolean;
  /** Called when the user dismisses the lightbox (backdrop / close / Escape). */
  readonly onClose: () => void;
}

type LoadState = 'loading' | 'loaded' | 'error';

const FOCUSABLE = 'button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function ImageLightbox({ src, alt, open, onClose }: ImageLightboxProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const captionId = useId();
  const [loadState, setLoadState] = useState<LoadState>('loading');

  // Reset the load state during render whenever the source changes or the
  // lightbox re-opens (the React-recommended "adjust state when a prop
  // changes" pattern via a tracked previous value — avoids both the
  // setState-in-effect cascade and the access-ref-in-render lint rules).
  const renderKey = `${open ? '1' : '0'}:${src}`;
  const [seenKey, setSeenKey] = useState(renderKey);
  if (open && renderKey !== seenKey) {
    setSeenKey(renderKey);
    setLoadState('loading');
  }

  // Lock body scroll and move focus into the dialog on open.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => closeRef.current?.focus(), 0);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const container = dialogRef.current;
    if (!container) return;
    const focusables = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusables.length === 0) {
      // Nothing to tab to — keep focus inside the dialog.
      e.preventDefault();
      return;
    }
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={alt ?? 'Image preview'}
      aria-describedby={alt ? captionId : undefined}
      onKeyDown={handleKey}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className={cn(
        'fixed inset-0 z-50 flex flex-col items-center justify-center p-6 gap-3',
        'bg-black/80 backdrop-blur-sm',
      )}
    >
      <button
        ref={closeRef}
        type="button"
        aria-label="Close"
        onClick={onClose}
        className={cn(
          'absolute top-4 end-4 size-10 rounded-full inline-flex items-center justify-center',
          'bg-surface/90 text-ink shadow-md hover:bg-surface transition-colors duration-[120ms]',
          'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/40',
        )}
      >
        <CloseIcon size={18} color="currentColor" />
      </button>

      <div
        className="relative flex items-center justify-center max-h-full max-w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {loadState === 'loading' ? (
          <div
            role="status"
            aria-live="polite"
            className="flex flex-col items-center gap-2 text-ink-3"
          >
            <span
              aria-hidden
              className="size-8 rounded-full border-2 border-hairline-2 border-t-accent animate-spin"
            />
            <span className="text-[12px] font-mono">Loading…</span>
          </div>
        ) : null}

        {loadState === 'error' ? (
          <div
            role="alert"
            className={cn(
              'flex flex-col items-center gap-2 px-6 py-8 rounded-lg',
              'bg-surface text-ink-2 border border-hairline-2 max-w-sm text-center',
            )}
          >
            <span className="text-danger">
              <AlertStatusIcon size={28} color="currentColor" />
            </span>
            <span className="text-[13px]">Could not load this image.</span>
          </div>
        ) : null}

        {/* The <img> is always mounted so onLoad/onError can fire; it's hidden until loaded. */}
        <img
          data-testid="lightbox-image"
          src={src}
          alt={alt ?? ''}
          onLoad={() => setLoadState('loaded')}
          onError={() => setLoadState('error')}
          className={cn(
            'max-h-[80vh] max-w-full object-contain rounded-lg shadow-2xl',
            loadState === 'loaded' ? 'block' : 'hidden',
          )}
        />
      </div>

      {alt && loadState !== 'error' ? (
        <p
          id={captionId}
          onClick={(e) => e.stopPropagation()}
          className="max-w-2xl text-center text-[12.5px] text-surface/90 leading-snug"
        >
          {alt}
        </p>
      ) : null}
    </div>,
    document.body,
  );
}
